import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  TrendingUp, ArrowLeft, RefreshCw, Calculator, DollarSign, Target, AlertTriangle,
  TrendingDown, Percent, BarChart3, Settings2, Lightbulb, ChevronRight, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ComposedChart
} from "recharts";

export default function BreakEvenPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState("overview");
  const [breakEvenData, setBreakEvenData] = useState(null);
  const [projectionData, setProjectionData] = useState(null);
  const [costClassifications, setCostClassifications] = useState([]);
  const [profitabilityData, setProfitabilityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [analysisMonths, setAnalysisMonths] = useState(12);
  
  // Scenario simulator state
  const [showScenarioDialog, setShowScenarioDialog] = useState(false);
  const [scenarioRevenue, setScenarioRevenue] = useState(0);
  const [scenarioFixedCost, setScenarioFixedCost] = useState(0);
  const [scenarioVariableCost, setScenarioVariableCost] = useState(0);
  const [scenarioResult, setScenarioResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [breakEvenRes, projectionRes, classificationsRes, profitabilityRes] = await Promise.all([
        api.get(`/reports/break-even?months=${analysisMonths}`),
        api.get("/reports/break-even/projection?months_ahead=6"),
        api.get("/reports/cost-classification"),
        api.get(`/reports/profitability?months=${analysisMonths}`)
      ]);
      setBreakEvenData(breakEvenRes.data);
      setProjectionData(projectionRes.data);
      setCostClassifications(classificationsRes.data);
      setProfitabilityData(profitabilityRes.data);
    } catch (error) {
      console.error("Error fetching break-even data:", error);
      toast.error("Failed to load break-even analysis");
    } finally {
      setIsLoading(false);
    }
  }, [analysisMonths]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCalculateScenario = async () => {
    setIsCalculating(true);
    try {
      const response = await api.post("/reports/break-even/scenario", {
        revenue_change: scenarioRevenue,
        fixed_cost_change: scenarioFixedCost,
        variable_cost_change: scenarioVariableCost
      });
      setScenarioResult(response.data);
    } catch (error) {
      console.error("Error calculating scenario:", error);
      toast.error("Failed to calculate scenario");
    } finally {
      setIsCalculating(false);
    }
  };

  const handleUpdateClassification = async (categoryId, classification) => {
    try {
      await api.put(`/reports/cost-classification/${categoryId}`, {
        classification: classification
      });
      toast.success("Classification updated");
      fetchData();
    } catch (error) {
      console.error("Error updating classification:", error);
      toast.error("Failed to update classification");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return `${value >= 0 ? "+" : ""}${value?.toFixed(1)}%`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3">
        <p className="font-medium text-sm mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background" data-testid="break-even-page">
        <header className="border-b bg-card">
          <div className="max-w-[1400px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-64" />
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-[400px]" />
        </main>
      </div>
    );
  }

  // Chart data for break-even visualization
  const breakEvenChartData = breakEvenData?.monthly_data?.map(m => ({
    month: m.month.substring(5),  // MM format
    Revenue: m.revenue,
    "Total Costs": m.total_costs,
    "Break-even": m.break_even_point,
    Profit: m.profit_loss
  })) || [];

  // Cost breakdown data
  const costBreakdownData = [
    { name: "Fixed Costs", value: breakEvenData?.total_fixed_costs || 0, fill: "hsl(195, 72%, 36%)" },
    { name: "Variable Costs", value: breakEvenData?.total_variable_costs || 0, fill: "hsl(38, 92%, 50%)" }
  ];

  // Projection chart data
  const projectionChartData = projectionData?.projections?.[0]?.months?.map((month, idx) => {
    const baseProj = projectionData.projections.find(p => p.scenario === "base");
    const optProj = projectionData.projections.find(p => p.scenario === "optimistic");
    const pessProj = projectionData.projections.find(p => p.scenario === "pessimistic");
    
    return {
      month: month.substring(5),
      "Base": baseProj?.revenue[idx] || 0,
      "Optimistic": optProj?.revenue[idx] || 0,
      "Pessimistic": pessProj?.revenue[idx] || 0,
      "Break-even": baseProj?.break_even_line[idx] || 0
    };
  }) || [];

  return (
    <div className="min-h-screen bg-background" data-testid="break-even-page">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="max-w-[1400px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Calculator className="h-6 w-6 text-primary" />
                  Break-even Analysis
                </h1>
                <p className="text-sm text-muted-foreground">
                  Calculate your break-even point and analyze profitability
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={analysisMonths.toString()} onValueChange={(v) => setAnalysisMonths(parseInt(v))}>
                <SelectTrigger className="w-[140px]" data-testid="months-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">Last 6 months</SelectItem>
                  <SelectItem value="12">Last 12 months</SelectItem>
                  <SelectItem value="24">Last 24 months</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                data-testid="refresh-button"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setScenarioRevenue(0);
                  setScenarioFixedCost(0);
                  setScenarioVariableCost(0);
                  setScenarioResult(null);
                  setShowScenarioDialog(true);
                }}
                data-testid="scenario-button"
              >
                <Settings2 className="h-4 w-4 mr-2" />
                What-If
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="break-even-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Target className="h-4 w-4 text-blue-500" />
                Break-even Point
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {formatCurrency(breakEvenData?.break_even_revenue)}/mo
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {formatCurrency(breakEvenData?.break_even_annual)} annually
              </p>
            </CardContent>
          </Card>

          <Card data-testid="margin-safety-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                {breakEvenData?.margin_of_safety >= 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                )}
                Margin of Safety
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${breakEvenData?.margin_of_safety >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                {breakEvenData?.margin_of_safety?.toFixed(1)}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {formatCurrency(Math.abs(breakEvenData?.margin_of_safety_amount || 0))} 
                {breakEvenData?.margin_of_safety >= 0 ? " above" : " below"} break-even
              </p>
            </CardContent>
          </Card>

          <Card data-testid="profitability-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Current Status
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Badge 
                  className={breakEvenData?.is_profitable 
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" 
                    : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                  }
                >
                  {breakEvenData?.is_profitable ? "Profitable" : "Loss"}
                </Badge>
              </div>
              <div className={`text-xl font-bold mt-1 ${breakEvenData?.is_profitable ? "text-green-600" : "text-red-600"}`}>
                {breakEvenData?.is_profitable ? "+" : ""}{formatCurrency(breakEvenData?.profit_loss_amount)}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="contribution-margin-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Percent className="h-4 w-4" />
                Contribution Margin
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {((breakEvenData?.contribution_margin_ratio || 0) * 100).toFixed(1)}%
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Progress 
                  value={(breakEvenData?.contribution_margin_ratio || 0) * 100} 
                  className="h-2 flex-1"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList data-testid="break-even-tabs">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="projections">Projections</TabsTrigger>
            <TabsTrigger value="costs">Cost Analysis</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Main Break-even Chart */}
              <Card className="lg:col-span-2" data-testid="break-even-chart">
                <CardHeader>
                  <CardTitle>Revenue vs Costs</CardTitle>
                  <CardDescription>Monthly comparison with break-even line</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={breakEvenChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="Revenue"
                          fill="hsl(158, 64%, 40%)"
                          fillOpacity={0.3}
                          stroke="hsl(158, 64%, 40%)"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="Total Costs"
                          stroke="hsl(4, 74%, 55%)"
                          strokeWidth={2}
                          dot={false}
                        />
                        <ReferenceLine
                          y={breakEvenData?.break_even_revenue || 0}
                          stroke="hsl(195, 72%, 36%)"
                          strokeDasharray="5 5"
                          label={{ value: "Break-even", position: "right" }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Cost Breakdown */}
              <Card data-testid="cost-breakdown-card">
                <CardHeader>
                  <CardTitle>Cost Structure</CardTitle>
                  <CardDescription>Fixed vs Variable costs</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Fixed Costs</span>
                      <span className="font-medium">{formatCurrency(breakEvenData?.total_fixed_costs)}</span>
                    </div>
                    <Progress 
                      value={(breakEvenData?.total_fixed_costs / ((breakEvenData?.total_fixed_costs || 0) + (breakEvenData?.total_variable_costs || 1))) * 100} 
                      className="h-3"
                    />
                    
                    <div className="flex justify-between items-center mt-4">
                      <span className="text-sm">Variable Costs</span>
                      <span className="font-medium">{formatCurrency(breakEvenData?.total_variable_costs)}</span>
                    </div>
                    <Progress 
                      value={(breakEvenData?.total_variable_costs / ((breakEvenData?.total_fixed_costs || 0) + (breakEvenData?.total_variable_costs || 1))) * 100} 
                      className="h-3 [&>div]:bg-amber-500"
                    />
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex justify-between items-center text-sm">
                      <span>Variable Cost Ratio</span>
                      <Badge variant="outline">
                        {((breakEvenData?.variable_cost_ratio || 0) * 100).toFixed(1)}%
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      For every $1 in revenue, {formatCurrency((breakEvenData?.variable_cost_ratio || 0))} goes to variable costs
                    </p>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex justify-between items-center text-sm">
                      <span>Avg Monthly Fixed</span>
                      <span className="font-medium">{formatCurrency(breakEvenData?.average_monthly_fixed)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-2">
                      <span>Avg Monthly Variable</span>
                      <span className="font-medium">{formatCurrency(breakEvenData?.average_monthly_variable)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Monthly Data Table */}
            <Card data-testid="monthly-data-table">
              <CardHeader>
                <CardTitle>Monthly Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Fixed Costs</TableHead>
                      <TableHead className="text-right">Variable Costs</TableHead>
                      <TableHead className="text-right">Total Costs</TableHead>
                      <TableHead className="text-right">Profit/Loss</TableHead>
                      <TableHead className="text-right">Cumulative</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {breakEvenData?.monthly_data?.slice(-6).map((month) => (
                      <TableRow key={month.month}>
                        <TableCell className="font-medium">{month.month}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(month.revenue)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(month.fixed_costs)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(month.variable_costs)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(month.total_costs)}</TableCell>
                        <TableCell className={`text-right font-medium ${month.profit_loss >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {month.profit_loss >= 0 ? "+" : ""}{formatCurrency(month.profit_loss)}
                        </TableCell>
                        <TableCell className={`text-right ${month.cumulative_profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {formatCurrency(month.cumulative_profit)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projections Tab */}
          <TabsContent value="projections" className="space-y-4">
            <Card data-testid="projection-chart">
              <CardHeader>
                <CardTitle>Revenue Projections</CardTitle>
                <CardDescription>6-month forecast with different scenarios</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={projectionChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="Base"
                        stroke="hsl(195, 72%, 36%)"
                        strokeWidth={2}
                        dot={{ fill: "hsl(195, 72%, 36%)" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="Optimistic"
                        stroke="hsl(158, 64%, 40%)"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                      />
                      <Line
                        type="monotone"
                        dataKey="Pessimistic"
                        stroke="hsl(4, 74%, 55%)"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                      />
                      <ReferenceLine
                        y={projectionData?.current_break_even || 0}
                        stroke="hsl(38, 92%, 50%)"
                        strokeDasharray="3 3"
                        label={{ value: "Break-even", position: "right" }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {projectionData?.projections?.map((proj) => (
                <Card key={proj.scenario} data-testid={`projection-${proj.scenario}`}>
                  <CardHeader>
                    <CardTitle className="text-lg">{proj.label}</CardTitle>
                    <CardDescription>
                      {proj.scenario === "base" ? "Current trends continue" :
                       proj.scenario === "optimistic" ? "20% higher growth" : "20% lower growth"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">End Revenue</span>
                        <span className="font-medium">{formatCurrency(proj.revenue?.[proj.revenue.length - 1])}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">End Profit</span>
                        <span className={`font-medium ${proj.profit?.[proj.profit.length - 1] >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {formatCurrency(proj.profit?.[proj.profit.length - 1])}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">vs Break-even</span>
                        <Badge variant={proj.revenue?.[proj.revenue.length - 1] >= projectionData?.current_break_even ? "default" : "destructive"}>
                          {proj.revenue?.[proj.revenue.length - 1] >= projectionData?.current_break_even ? "Above" : "Below"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Cost Analysis Tab */}
          <TabsContent value="costs" className="space-y-4">
            <Card data-testid="cost-classification-table">
              <CardHeader>
                <CardTitle>Cost Classifications</CardTitle>
                <CardDescription>
                  Classify your expense categories as fixed or variable to improve break-even accuracy
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Actual Amount</TableHead>
                      <TableHead>Classification</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costClassifications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No expense categories found. Add categories in your transactions to classify costs.
                        </TableCell>
                      </TableRow>
                    ) : (
                      costClassifications.map((cat) => (
                        <TableRow key={cat.category_id}>
                          <TableCell className="font-medium">{cat.category_name}</TableCell>
                          <TableCell className="text-right">{formatCurrency(cat.actual_amount)}</TableCell>
                          <TableCell>
                            <Select
                              value={cat.classification}
                              onValueChange={(v) => handleUpdateClassification(cat.category_id, v)}
                            >
                              <SelectTrigger className="w-[120px]" data-testid={`classify-${cat.category_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="fixed">Fixed</SelectItem>
                                <SelectItem value="variable">Variable</SelectItem>
                                <SelectItem value="mixed">Mixed</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {cat.classification === "fixed" ? "Doesn't change with revenue" :
                             cat.classification === "variable" ? "Scales with revenue" :
                             "Partially scales with revenue"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Profitability Metrics */}
            <Card data-testid="profitability-metrics">
              <CardHeader>
                <CardTitle>Profitability Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{profitabilityData?.gross_margin_percentage?.toFixed(1)}%</div>
                    <div className="text-sm text-muted-foreground">Gross Margin</div>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{profitabilityData?.operating_margin_percentage?.toFixed(1)}%</div>
                    <div className="text-sm text-muted-foreground">Operating Margin</div>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{profitabilityData?.profitable_months}/{profitabilityData?.total_months}</div>
                    <div className="text-sm text-muted-foreground">Profitable Months</div>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{profitabilityData?.sustainability_score?.toFixed(0)}</div>
                    <div className="text-sm text-muted-foreground">Sustainability Score</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-muted-foreground">Best Month</div>
                    <div className="text-lg font-bold text-green-600">{formatCurrency(profitabilityData?.best_month_profit)}</div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-muted-foreground">Worst Month</div>
                    <div className="text-lg font-bold text-red-600">{formatCurrency(profitabilityData?.worst_month_profit)}</div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-muted-foreground">Avg Monthly Profit</div>
                    <div className={`text-lg font-bold ${profitabilityData?.average_monthly_profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {formatCurrency(profitabilityData?.average_monthly_profit)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Break-even Insights */}
              <Card data-testid="break-even-insights">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-amber-500" />
                    Break-even Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {breakEvenData?.insights?.map((insight, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                        <ChevronRight className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <p className="text-sm">{insight}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Profitability Recommendations */}
              <Card data-testid="profitability-recommendations">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-500" />
                    Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {profitabilityData?.recommendations?.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                        <ChevronRight className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <p className="text-sm">{rec}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Key Metrics Summary */}
            <Card data-testid="key-metrics-summary">
              <CardHeader>
                <CardTitle>Key Metrics Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-muted-foreground">Revenue Trend</div>
                    <div className="flex items-center gap-2 mt-1">
                      {breakEvenData?.revenue_trend === "growing" ? (
                        <TrendingUp className="h-5 w-5 text-green-500" />
                      ) : breakEvenData?.revenue_trend === "declining" ? (
                        <TrendingDown className="h-5 w-5 text-red-500" />
                      ) : (
                        <BarChart3 className="h-5 w-5 text-blue-500" />
                      )}
                      <span className="font-medium capitalize">{breakEvenData?.revenue_trend}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatPercent(breakEvenData?.revenue_growth_rate || 0)}
                    </div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-muted-foreground">Monthly Revenue</div>
                    <div className="text-lg font-bold mt-1">{formatCurrency(breakEvenData?.average_monthly_revenue)}</div>
                    <div className="text-xs text-muted-foreground">avg per month</div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-muted-foreground">Total Revenue</div>
                    <div className="text-lg font-bold mt-1">{formatCurrency(breakEvenData?.total_revenue)}</div>
                    <div className="text-xs text-muted-foreground">{breakEvenData?.analysis_period}</div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm text-muted-foreground">Projected Annual</div>
                    <div className={`text-lg font-bold mt-1 ${breakEvenData?.projected_annual_profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {formatCurrency(breakEvenData?.projected_annual_profit)}
                    </div>
                    <div className="text-xs text-muted-foreground">profit projection</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Scenario Simulator Dialog */}
      <Dialog open={showScenarioDialog} onOpenChange={setShowScenarioDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>What-If Scenario Simulator</DialogTitle>
            <DialogDescription>
              Adjust variables to see how changes would affect your break-even point
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Revenue Change */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Revenue Change</Label>
                <span className={`font-medium ${scenarioRevenue >= 0 ? "text-green-600" : "text-red-600"}`}>
                  {scenarioRevenue >= 0 ? "+" : ""}{scenarioRevenue}%
                </span>
              </div>
              <Slider
                value={[scenarioRevenue]}
                onValueChange={([v]) => setScenarioRevenue(v)}
                min={-50}
                max={100}
                step={5}
                className="w-full"
                data-testid="scenario-revenue-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>-50%</span>
                <span>0%</span>
                <span>+100%</span>
              </div>
            </div>

            {/* Fixed Cost Change */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Fixed Cost Change</Label>
                <span className={`font-medium ${scenarioFixedCost <= 0 ? "text-green-600" : "text-red-600"}`}>
                  {scenarioFixedCost >= 0 ? "+" : ""}{scenarioFixedCost}%
                </span>
              </div>
              <Slider
                value={[scenarioFixedCost]}
                onValueChange={([v]) => setScenarioFixedCost(v)}
                min={-50}
                max={50}
                step={5}
                className="w-full"
                data-testid="scenario-fixed-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>-50%</span>
                <span>0%</span>
                <span>+50%</span>
              </div>
            </div>

            {/* Variable Cost Change */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Variable Cost Change</Label>
                <span className={`font-medium ${scenarioVariableCost <= 0 ? "text-green-600" : "text-red-600"}`}>
                  {scenarioVariableCost >= 0 ? "+" : ""}{scenarioVariableCost}%
                </span>
              </div>
              <Slider
                value={[scenarioVariableCost]}
                onValueChange={([v]) => setScenarioVariableCost(v)}
                min={-50}
                max={50}
                step={5}
                className="w-full"
                data-testid="scenario-variable-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>-50%</span>
                <span>0%</span>
                <span>+50%</span>
              </div>
            </div>

            {/* Scenario Result */}
            {scenarioResult && (
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="text-sm font-medium">{scenarioResult.scenario_name}</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">New Break-even:</span>
                    <span className="ml-2 font-medium">{formatCurrency(scenarioResult.new_break_even_revenue)}/mo</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Margin of Safety:</span>
                    <span className="ml-2 font-medium">{scenarioResult.new_margin_of_safety}%</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Profit/Loss:</span>
                    <span className={`ml-2 font-medium ${scenarioResult.is_profitable ? "text-green-600" : "text-red-600"}`}>
                      {formatCurrency(scenarioResult.new_profit_loss)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Change:</span>
                    <span className={`ml-2 font-medium ${scenarioResult.change_from_current >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {formatPercent(scenarioResult.change_from_current)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScenarioDialog(false)}>
              Close
            </Button>
            <Button onClick={handleCalculateScenario} disabled={isCalculating} data-testid="calculate-scenario-button">
              {isCalculating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
              Calculate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
